// src/crm/ugc/ProTab.tsx
//
// Onglet « Pro à activer » du programme UGC (Pro offert aux membres) : les demandes en attente
// (waitsForPro : nom, adresse du compte dans l'app, date de la demande, lien vers la fiche où
// noter « Pro activé jusqu'au », et « Classer » quand le compte est introuvable dans RevenueCat),
// puis les Pro à retirer aux membres partis. L'activation reste manuelle dans RevenueCat.

import { Link } from 'react-router-dom'
import { proToRemove, waitsForPro } from '../../../functions/lib/ugc/pro'
import type { UgcMember } from '../../lib/ugcTypes'
import { formatDate } from './format'
import { usePatchMember, useUgcMembers } from './hooks'

function parisToday(now: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(now)
}

function DismissButton({ member }: { member: UgcMember }) {
  const patch = usePatchMember()
  return (
    <>
      <button
        type="button"
        className="ugc-btn"
        aria-label={`Classer la demande de ${member.displayName}`}
        disabled={patch.isPending}
        onClick={() => patch.mutate({ id: member.id, patch: { dismissProRequest: true } })}
      >
        Classer
      </button>
      {patch.error && (
        <p role="alert" className="ugc-error">
          {patch.error.message}
        </p>
      )}
    </>
  )
}

export function ProTab() {
  const members = useUgcMembers()
  const today = parisToday(new Date())
  const all = members.data ?? []
  const pending = all
    .filter((m) => waitsForPro(m))
    .sort((a, b) => (a.proRequestedAt ?? '').localeCompare(b.proRequestedAt ?? ''))
  const removals = all.filter((m) => proToRemove(m, today))

  return (
    <section className="ugc-tab-panel" aria-label="Pro à activer">
      <p className="ugc-muted">
        Accorde le Pro dans RevenueCat à l'adresse donnée, puis note « Pro activé jusqu'au » dans la fiche. « Classer » quand
        le compte est introuvable : la personne est invitée à vérifier son adresse.
      </p>
      {members.isPending && <p className="ugc-muted">Chargement…</p>}
      {members.isError && <p className="ugc-error">Les membres n'ont pas pu être chargés : {members.error.message}</p>}
      {members.isSuccess && pending.length === 0 && <p className="ugc-empty">Aucune demande de Pro en attente.</p>}
      {pending.length > 0 && (
        <div className="ugc-table-wrap">
          <table className="ugc-table" aria-label="Pro à activer">
            <thead>
              <tr>
                <th scope="col">Personne</th>
                <th scope="col">Compte dans l'app</th>
                <th scope="col">Demandé le</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((m) => (
                <tr key={m.id}>
                  <td>{m.displayName}</td>
                  <td>{m.appAccountEmail}</td>
                  <td>{m.proRequestedAt ? formatDate(m.proRequestedAt) : ''}</td>
                  <td>
                    <div className="ugc-actions">
                    <Link className="ugc-link" to={`/crm/contacts/${encodeURIComponent(m.creatorId)}`} aria-label={`Fiche de ${m.displayName}`}>
                      Fiche
                    </Link>
                    <DismissButton member={m} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {removals.length > 0 && (
        <>
          <h2 className="ugc-subtitle">Pro à retirer (membres partis)</h2>
          <div className="ugc-table-wrap">
            <table className="ugc-table" aria-label="Pro à retirer">
              <thead>
                <tr>
                  <th scope="col">Personne</th>
                  <th scope="col">Compte dans l'app</th>
                  <th scope="col">Pro jusqu'au</th>
                </tr>
              </thead>
              <tbody>
                {removals.map((m) => (
                  <tr key={m.id}>
                    <td>{m.displayName}</td>
                    <td>{m.appAccountEmail ?? m.previousAppAccountEmail}</td>
                    <td>{m.proUntil ? formatDate(m.proUntil) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}
